import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { SegmentationData, SegmentRegion } from './types';
import { ServerSegmentationResult } from '@/api/api';

const SEGMENT_IMAGE_QUERY_KEY = 'imageEditor.segmentImage';

const PASTEL_PALETTE = [
  { r: 255, g: 179, b: 186 },
  { r: 186, g: 225, b: 255 },
  { r: 186, g: 255, b: 201 },
  { r: 255, g: 223, b: 186 },
  { r: 218, g: 186, b: 255 },
  { r: 255, g: 255, b: 186 },
  { r: 186, g: 255, b: 255 },
  { r: 255, g: 186, b: 255 },
  { r: 210, g: 255, b: 186 },
  { r: 255, g: 210, b: 220 },
  { r: 186, g: 210, b: 255 },
  { r: 230, g: 255, b: 210 },
];

function decodeServerResult(result: ServerSegmentationResult): SegmentationData {
  const bytes = Uint8Array.from(atob(result.regionMap), c => c.codePointAt(0)!);
  const regionMap = new Uint16Array(bytes.buffer);

  const regions: SegmentRegion[] = result.regions.map(r => ({
    id: r.id,
    pixelCount: r.pixelCount,
    color: PASTEL_PALETTE[r.id % PASTEL_PALETTE.length],
  }));

  return {
    regionMap,
    width: result.width,
    height: result.height,
    regions,
  };
}

async function fileToImage(file?: File): Promise<HTMLImageElement> {
  if (!file) throw new Error('File is required');
  const url = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => {
      URL.revokeObjectURL(url);
      resolve(img);
    });
    img.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    });
    img.src = url;
  });
}

async function imageToBlob(image: HTMLImageElement): Promise<File> {
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Failed to get canvas context');
  context.drawImage(image, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) return reject(new Error('Failed to create blob'));
      resolve(new File([blob], 'image.png', { type: 'image/png' }));
    }, 'image/png');
  });
}

export const useSegmentation = ({
  file,
  numberSegments,
  sensitivity,
}: {
  file?: File;
  numberSegments: number;
  sensitivity: number;
}) => {
  const { getTenantId } = useTenants();

  const { data, isFetching } = useQuery({
    queryKey: [
      SEGMENT_IMAGE_QUERY_KEY,
      { fileName: file?.name, fileSize: file?.size, lastModified: file?.lastModified, numberSegments, sensitivity },
    ],
    queryFn: async () => {
      const image = await fileToImage(file);
      const blob = await imageToBlob(image);
      const response = await api.assets.segmentImage(getTenantId(), blob, numberSegments, sensitivity);
      return decodeServerResult(response.data);
    },
    enabled: !!file,
    staleTime: Infinity,
  });

  return { segmentation: data, isSegmenting: isFetching };
};
